import * as React from "react";
import { useTranslation } from "react-i18next";
import { useMemo, useEffect, useCallback } from "react";
import styled, { css } from "styled-components";
import useTableLayout from "components/common/EntityDataTable/hooks/useTableLayout";
import useUpdateUserLayoutPreferences from "components/common/EntityDataTable/hooks/useUpdateUserLayoutPreferences";
import { useTableEventHandlers } from "components/common/EntityDataTable";
import {
  Spinner,
  PaginatedList,
  SearchForm,
  NoSearchResult,
  EntityDataTable,
  FetchErrorAlert
} from "components/common";
import EntityFilters from "components/common/EntityFilters";
import TableFetchContextProvider from "components/common/PaginatedEntityTable/TableFetchContextProvider";
import TableFilterProvider from "components/common/PaginatedEntityTable/TableFilterProvider";
import useFetchEntities from "components/common/PaginatedEntityTable/useFetchEntities";
import useOnRefresh from "components/common/PaginatedEntityTable/useOnRefresh";
import Slicing from "components/common/PaginatedEntityTable/slicing";
import useAuthorizedAttributes from "components/common/PaginatedEntityTable/hooks/useAuthorizedAttributes";
import useServerText from "util/serverText";
import {
  ALPHABETICAL_SORT,
  defaultSortDirectionForMode
} from "components/common/PaginatedEntityTable/slicing/slicingConstants";
import { useWithLocalState, useWithURLParams } from "./useFiltersAndPagination";
import useTableFilterContext from "./useTableFilterContext";
const SearchRow = styled.div(
  ({ $compactView }) => css`
    margin-bottom: 5px;
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;

    ${$compactView && css`
      flex-wrap: wrap;

      .compact-search-form {
        flex: 1;
        min-width: 0;
      }

      .compact-search-form .form-group,
      .compact-search-form .input-container,
      .compact-search-form .query {
        display: block;
        width: 100%;
      }
    `}
  `
);
const Container = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.sm};
    width: 100%;
  `
);
const RowContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacings.sm};
    width: 100%;
  `
);
const TableWrapper = styled.div(
  ({ $compactView }) => css`
    display: flex;
    flex-direction: column;
    overflow-x: auto;
    width: 100%;
    ${$compactView && "min-width: 0;"}
  `
);
const INITIAL_DATA = {
  pagination: { total: 0 },
  list: [],
  attributes: [],
  meta: null
};
const defaultSlicingPreferences = (sliceColumn, columnSchemas) => {
  var _a, _b;
  if (sliceColumn === null)
    return {
      sliceColumn: null,
      sortBy: null,
      order: null
    };
  const sliceSortDefault = (_a = columnSchemas.find(({ id }) => id === sliceColumn)) == null ? void 0 : _a.slice_sort_default;
  const sortBy = (_b = sliceSortDefault == null ? void 0 : sliceSortDefault.mode) != null ? _b : ALPHABETICAL_SORT;
  return {
    sliceColumn,
    sortBy,
    order: defaultSortDirectionForMode(sortBy, sliceSortDefault)
  };
};
const PaginatedEntityTableInner = ({
  additionalAttributes = [],
  bulkSelection = void 0,
  columnRenderers,
  entityActions = void 0,
  entityAttributesAreCamelCase,
  expandedSectionRenderers = void 0,
  rowOverride = void 0,
  externalSearch = void 0,
  fetchEntities,
  fetchSlices = void 0,
  filterValueRenderers = void 0,
  focusSearchAfterMount = false,
  humanName,
  isLoadingLayoutPreferences,
  keyFn,
  layoutConfig,
  middleSection: MiddleSection = void 0,
  topSection: TopSection = void 0,
  onDataLoaded = void 0,
  queryHelpComponent = void 0,
  reactQueryOptions,
  searchPlaceholder = void 0,
  sliceRenderers = void 0,
  tableLayout,
  topRightCol = void 0,
  withoutURLParams = false,
  noPageSizeSelect = false,
  noColumnReordering = false,
  compactView = false
}) => {
  var _a;
  const { t } = useTranslation("common");
  const { searchParams, setQuery, onChangeFilters, onChangeSlicingFilter, paginationState } = useTableFilterContext();
  const { mutateAsync: updateTableLayout, resetAsync: resetTableLayout } = useUpdateUserLayoutPreferences(
    tableLayout.entityTableId,
    tableLayout.layoutVariant
  );
  const fetchKey = useMemo(() => keyFn(searchParams), [searchParams, keyFn]);
  const {
    data: paginatedEntities = INITIAL_DATA,
    isInitialLoading: isLoadingEntities,
    refetch,
    isError,
    error
  } = useFetchEntities({
    fetchKey,
    searchParams,
    enabled: !isLoadingLayoutPreferences,
    fetchEntities,
    humanName,
    fetchOptions: reactQueryOptions
  });
  useOnRefresh(refetch);
  useEffect(() => {
    if (!onDataLoaded || isLoadingEntities) {
      return;
    }
    onDataLoaded(paginatedEntities);
  }, [isLoadingEntities, onDataLoaded, paginatedEntities]);
  const appSection = `${tableLayout.entityTableId}-list`;
  const {
    onLayoutPreferencesChange,
    onPageSizeChange,
    onResetLayoutPreferences,
    onSearch,
    onSearchReset,
    onSortChange
  } = useTableEventHandlers({
    appSection,
    paginationQueryParameter: paginationState,
    updateTableLayout,
    setQuery,
    resetTableLayout
  });
  const serverText = useServerText();
  const serverAttributes = useMemo(
    () => {
      var _a2;
      return ((_a2 = paginatedEntities == null ? void 0 : paginatedEntities.attributes) != null ? _a2 : []).map((attribute) => ({ ...attribute, title: serverText(attribute.title) }));
    },
    [paginatedEntities == null ? void 0 : paginatedEntities.attributes, serverText]
  );
  const columnSchemas = useMemo(
    () => [...serverAttributes, ...additionalAttributes.map((attribute) => ({ isDerived: true, ...attribute }))],
    [additionalAttributes, serverAttributes]
  );
  const onSlicingPreferencesChange = useCallback(
    (slicing) => updateTableLayout({ slicing }),
    [updateTableLayout]
  );
  const onSlicingLayout = useCallback(
    (newSliceCol, newSlice) => {
      onChangeSlicingFilter(newSlice);
      if (newSliceCol === searchParams.sliceCol) {
        return;
      }
      updateTableLayout({
        slicing: newSliceCol !== void 0 ? defaultSlicingPreferences(newSliceCol, columnSchemas) : null
      });
    },
    [columnSchemas, searchParams.sliceCol, onChangeSlicingFilter, updateTableLayout]
  );
  const attributes = useAuthorizedAttributes(serverAttributes);
  const onPaginationChange = withoutURLParams ? (currentPage, pageSize) => paginationState.setPagination({ page: currentPage, pageSize }) : void 0;
  if (isLoadingLayoutPreferences || isLoadingEntities) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const {
    list,
    meta,
    pagination: { total }
  } = paginatedEntities;
  return /* @__PURE__ */ React.createElement(
    TableFetchContextProvider,
    {
      refetch,
      attributes,
      entityTableId: tableLayout.entityTableId,
      layoutVariant: tableLayout.layoutVariant
    },
    /* @__PURE__ */ React.createElement(Container, null, TopSection ? /* @__PURE__ */ React.createElement(TopSection, null) : null, /* @__PURE__ */ React.createElement(RowContainer, null, searchParams.sliceCol && typeof fetchSlices === "function" && /* @__PURE__ */ React.createElement(
      Slicing,
      {
        fetchSlices,
        appSection,
        columnSchemas,
        sliceRenderers,
        onChangeSlicing: onSlicingLayout,
        onSlicingPreferencesChange,
        slicingPreferences: layoutConfig.slicing
      }
    ), /* @__PURE__ */ React.createElement(TableWrapper, { $compactView: compactView }, !externalSearch && /* @__PURE__ */ React.createElement(SearchRow, { $compactView: compactView }, /* @__PURE__ */ React.createElement(
      SearchForm,
      {
        className: compactView ? "compact-search-form" : void 0,
        focusAfterMount: focusSearchAfterMount,
        onSearch,
        onReset: onSearchReset,
        query: searchParams.query,
        placeholder: searchPlaceholder != null ? searchPlaceholder : t("entity_table.search_placeholder", { humanName }),
        queryHelpComponent
      },
      attributes.length > 0 && /* @__PURE__ */ React.createElement("div", { style: { marginBottom: 5 } }, /* @__PURE__ */ React.createElement(
        EntityFilters,
        {
          attributes,
          urlQueryFilters: searchParams.filters,
          setUrlQueryFilters: onChangeFilters,
          filterValueRenderers,
          appSection,
          activeSliceCol: searchParams.sliceCol,
          activeSlice: searchParams.slice
        }
      ))
    ), topRightCol), MiddleSection ? /* @__PURE__ */ React.createElement(MiddleSection, { searchParams, setFilters: onChangeFilters }) : null, isError ? /* @__PURE__ */ React.createElement(FetchErrorAlert, { message: t("entity_table.fetch_failed", { humanName }), error }) : /* @__PURE__ */ React.createElement(
      PaginatedList,
      {
        pageSize: layoutConfig.pageSize,
        showPageSizeSelect: false,
        compactPagination: compactView,
        totalItems: total,
        useQueryParameter: !withoutURLParams,
        onChange: onPaginationChange
      },
      (list == null ? void 0 : list.length) === 0 ? /* @__PURE__ */ React.createElement(NoSearchResult, null, t("entity_table.no_results", { humanName })) : /* @__PURE__ */ React.createElement(
        EntityDataTable,
        {
          entities: list,
          defaultDisplayedColumns: tableLayout.defaultDisplayedAttributes,
          layoutPreferences: {
            attributes: layoutConfig.attributes,
            order: layoutConfig.order
          },
          defaultColumnOrder: tableLayout.defaultColumnOrder,
          onResetLayoutPreferences,
          onLayoutPreferencesChange,
          onChangeSlicing: onSlicingLayout,
          expandedSectionRenderers,
          rowOverride,
          enableSlicing: typeof fetchSlices === "function" && !((_a = layoutConfig == null ? void 0 : layoutConfig.slicing) == null ? void 0 : _a.readOnly),
          bulkSelection,
          onSortChange,
          onPageSizeChange,
          pageSize: layoutConfig.pageSize,
          activeSort: layoutConfig.sort,
          activeSliceCol: searchParams.sliceCol,
          appSection,
          entityActions,
          columnRenderers,
          columnSchemas,
          entityAttributesAreCamelCase,
          meta,
          noPageSizeSelect,
          noColumnReordering
        }
      )
    ))))
  );
};
const TableWithLocalState = ({ ...props }) => {
  const contextValue = useWithLocalState(props.layoutConfig, props.defaultFilters);
  return /* @__PURE__ */ React.createElement(TableFilterProvider, { value: contextValue, externalSearch: props.externalSearch }, /* @__PURE__ */ React.createElement(PaginatedEntityTableInner, { ...props }));
};
const TableWithURLParams = ({ ...props }) => {
  const contextValue = useWithURLParams(props.layoutConfig, props.defaultFilters);
  return /* @__PURE__ */ React.createElement(TableFilterProvider, { value: contextValue, externalSearch: props.externalSearch }, /* @__PURE__ */ React.createElement(PaginatedEntityTableInner, { ...props }));
};
const PaginatedEntityTable = ({
  fetchOptions: reactQueryOptions = void 0,
  ...props
}) => {
  const { layoutConfig, isInitialLoading: isLoadingLayoutPreferences } = useTableLayout(props.tableLayout);
  const Wrapper = props.withoutURLParams ? TableWithLocalState : TableWithURLParams;
  return /* @__PURE__ */ React.createElement(
    Wrapper,
    {
      ...props,
      layoutConfig,
      isLoadingLayoutPreferences,
      reactQueryOptions,
      onDataLoaded: props.onDataLoaded
    }
  );
};
export default PaginatedEntityTable;
